import { z } from "zod";
import { defineEndpoint, idParamsSchema } from "./endpoint.js";
/**
 * Data export and account closure (docs/specs/account-closure.md, SA-20, PRD §6.5): the Owner downloads every record
 * of the business as a ZIP, and may close the account — 30 days to change their mind, then permanent deletion.
 */
const tag = { tag: "account" };
const reason = z.string().trim().min(1).max(500);
// Data export ---------------------------------------------------------------------------------------------------
export const DATA_EXPORT_STATUSES = ["queued", "running", "done", "failed", "expired"];
export const dataExportSchema = z.object({
    id: z.uuid(),
    status: z.enum(DATA_EXPORT_STATUSES),
    tableCount: z.number().int().nonnegative(),
    rowCount: z.number().int().nonnegative(),
    fileCount: z.number().int().nonnegative(),
    /** Attached files left out because the ZIP reached its size cap (listed in its manifest). */
    skippedFileCount: z.number().int().nonnegative(),
    sizeBytes: z.number().int().nonnegative(),
    /** Signed link (15 minutes) while the export is `done`; null otherwise. */
    downloadUrl: z.url().nullable(),
    error: z.string().nullable(),
    createdAt: z.iso.datetime(),
    finishedAt: z.iso.datetime().nullable(),
    /** The ZIP is deleted after this time (status becomes `expired`). */
    expiresAt: z.iso.datetime().nullable(),
});
export const dataExportListSchema = z.object({ data: z.array(dataExportSchema) });
export const listDataExports = defineEndpoint({
    ...tag,
    name: "listDataExports",
    method: "GET",
    path: "/v1/data-exports",
    access: { permission: "account.export" },
    summary: "The business's data exports, newest first",
    status: 200,
    response: dataExportListSchema,
});
export const startDataExport = defineEndpoint({
    ...tag,
    name: "startDataExport",
    method: "POST",
    path: "/v1/data-exports",
    access: { permission: "account.export" },
    summary: "Export every record of the business as a ZIP of CSV files (a job)",
    status: 202,
    response: dataExportSchema,
});
// Account closure -----------------------------------------------------------------------------------------------
export const accountClosureSchema = z.object({
    status: z.enum(["open", "closing"]),
    /** The business's web address: what the Owner types to confirm. */
    slug: z.string(),
    requestedAt: z.iso.datetime().nullable(),
    requestedBy: z.uuid().nullable(),
    /** Everything is deleted on or after this time. */
    dueAt: z.iso.datetime().nullable(),
    reason: z.string().nullable(),
    /** Days of grace a closure gets (30). */
    graceDays: z.number().int().positive(),
});
export const requestAccountClosureSchema = z.object({
    reason,
    /** The business's web address (slug), typed by the Owner to confirm. */
    confirmSlug: z.string().trim().min(1).max(63),
});
export const getAccountClosure = defineEndpoint({
    ...tag,
    name: "getAccountClosure",
    method: "GET",
    path: "/v1/account-closure",
    access: { signedIn: true },
    summary: "Whether the business is closing, and when its data will be deleted",
    status: 200,
    response: accountClosureSchema,
});
export const requestAccountClosure = defineEndpoint({
    ...tag,
    name: "requestAccountClosure",
    method: "POST",
    path: "/v1/account-closure",
    access: { permission: "account.close" },
    summary: "Close the account: 30 days of grace, then every record is deleted",
    body: requestAccountClosureSchema,
    status: 200,
    response: accountClosureSchema,
});
export const cancelAccountClosure = defineEndpoint({
    ...tag,
    name: "cancelAccountClosure",
    method: "POST",
    path: "/v1/account-closure/cancel",
    access: { permission: "account.close" },
    summary: "Keep the account: cancel a closure during its grace period",
    status: 200,
    response: accountClosureSchema,
});
// Super Admin ---------------------------------------------------------------------------------------------------
export const platformClosureSchema = z.object({
    tenantId: z.uuid(),
    name: z.string(),
    slug: z.string(),
    requestedAt: z.iso.datetime(),
    dueAt: z.iso.datetime(),
    reason: z.string().nullable(),
});
/** The deletion certificate kept in the platform audit log after a business is deleted. */
export const deletionCertificateSchema = z.object({
    tenantId: z.uuid(),
    slug: z.string(),
    deletedAt: z.iso.datetime(),
    tables: z.number().int().nonnegative(),
    rows: z.number().int().nonnegative(),
});
export const platformClosureListSchema = z.object({
    closing: z.array(platformClosureSchema),
    deleted: z.array(deletionCertificateSchema),
});
export const listPlatformClosures = defineEndpoint({
    tag: "platform-admin",
    name: "listPlatformClosures",
    method: "GET",
    path: "/v1/platform/closures",
    access: { platformAdmin: true },
    summary: "Businesses closing (grace period) and the deletion certificates of deleted ones",
    status: 200,
    response: platformClosureListSchema,
});
export const cancelPlatformClosure = defineEndpoint({
    tag: "platform-admin",
    name: "cancelPlatformClosure",
    method: "POST",
    path: "/v1/platform/tenants/{id}/closure/cancel",
    access: { platformAdmin: true },
    summary: "Cancel a business's closure during its grace period (the Owner asked support)",
    params: idParamsSchema,
    body: z.object({ reason }),
    status: 200,
    response: accountClosureSchema,
});
//# sourceMappingURL=account-data.js.map